import React, { useContext, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import colorSwatchesQuery from 'src/queries/productListColorSwatches.graphql';
import { ColourPickerSwatch } from 'src/components/ProductDetail/utils/product-detail.types';

export type ColorSwatchesProduct = {
    id: number;
    color_swatches: ColourPickerSwatch[] | null;
};

type ContextType = {
    loading: boolean;
    products: Map<number, ColorSwatchesProduct>;
};

const ColorSwatchesContext = React.createContext<ContextType>({ loading: false, products: new Map() });

/**
 * Fetches the colour swatches for every product in a list with a single
 * `productsById` request, instead of one request per ProductListColourPicker.
 * Client-side only, so it never holds up SSR of the list itself.
 */
export const ColorSwatchesProvider: React.FC<React.PropsWithChildren<{ ids: (number | string)[] }>> = ({
    ids,
    children,
}) => {
    // sorted + deduped so re-sorting the same page reuses the cached response
    const key = [...new Set(ids.map(Number).filter(Boolean))].sort((a, b) => a - b).join(',');
    const variables = useMemo(() => ({ ids: key ? key.split(',').map(Number) : [] }), [key]);

    const { data, loading } = useQuery<{ productDetail: ColorSwatchesProduct[] }>(colorSwatchesQuery, {
        variables,
        ssr: false,
        skip: variables.ids.length === 0,
        fetchPolicy: 'cache-first',
    });

    const value = useMemo(
        () => ({
            loading,
            products: new Map((data?.productDetail || []).map((product) => [Number(product.id), product])),
        }),
        [data, loading],
    );

    return <ColorSwatchesContext.Provider value={value}>{children}</ColorSwatchesContext.Provider>;
};

export const useProductColorSwatches = (productId: number) => {
    const { loading, products } = useContext(ColorSwatchesContext);
    return { loading, product: products.get(Number(productId)) };
};
